import Dexie from 'dexie';
import { appLocalStorage, storageName } from '../utils/storage';
import config from '../utils/config';
import { indexedDbOperation } from '../utils/indexedDbOperation';
import { deleteOwnerMediaDrafts } from '../media/drafts';
import { forgetOwnerDraftIdentities } from '../media/draftIdentity';
import { deleteOwnerDrawingDrafts } from '../drawing/store';
import { forgetOwnerPalette } from '../drawing/palette';
import { forgetClaimedGuestDrafts } from '../guestTrial/store';
import { forgetOwnerOnboarding, markExternalEntry } from '../onboarding/state';
import { discardAuthAttempts } from '../utils/authAttemptStorage';
import { invalidateFeedSnapshots } from '../utils/searchFeedReturn';
import { localDeletionKey, markDeletedOwner } from './ownerState';

const news = new Dexie(storageName('NewsDatabase'));
news.version(1).stores({ news: 'id, rang, viewed, viewedAt' });
const running = new Map();
const ownedKey = (key, id) => [
  `comment:${id}:`, `comment-pending:${id}:`, `comment-quote:${id}:`,
  `comment-guest-import:${id}:`, `opinion-draft:${id}:`, `protection-restore:${id}:`,
].some(prefix => key.startsWith(prefix)) || key === `drawing-palette:${id}`
  || key === `onboarding:account:${id}` || key === `onboarding:account:${id}:skip-visit`;

function clearStorageOwner(storage, id) {
  const namespace = storageName('');
  const keys = [];
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key?.startsWith(namespace) && ownedKey(key.slice(namespace.length), id)) keys.push(key);
  }
  keys.forEach(key => storage.removeItem(key));
}

/** This operation is irreversible locally and requires explicit server proof.
 * The caller manages logout/navigation separately, using its captured token.
 * In particular, cleaning A must never sign out B on a shared device. */
export function cleanupDeletedOwner(userId, token) {
  const id = Number(userId);
  if (!Number.isSafeInteger(id) || id < 1) return Promise.reject(new Error('INVALID_DELETED_OWNER'));
  if (running.has(id)) return running.get(id);
  // Fence autosave/unmount synchronously, before any asynchronous DB cleanup.
  const durable = markDeletedOwner(id);
  const operation = (async () => {
    let failed = false;
    forgetOwnerDraftIdentities(id);
    forgetOwnerPalette(id);
    forgetOwnerOnboarding(id);
    invalidateFeedSnapshots();
    try {
      if (!forgetClaimedGuestDrafts(id)) failed = true;
      clearStorageOwner(window.localStorage, id);
      clearStorageOwner(window.sessionStorage, id);
      // Removing this owner's drafts can free space after an initial quota
      // error. Persist the proof before awaiting any IndexedDB operation.
      if (!durable) appLocalStorage.setItem(localDeletionKey(id), 'pending');
      const user = JSON.parse(appLocalStorage.getItem(config.user) || 'null');
      if (user?.id === id && (token === undefined || token === appLocalStorage.getItem(config.token))) {
        discardAuthAttempts();
        markExternalEntry(); // Deletion must not replay onboarding for this visit.
      }
    } catch { failed = true; }
    const cleanups = await Promise.allSettled([
      deleteOwnerMediaDrafts(id), deleteOwnerDrawingDrafts(id),
      indexedDbOperation(news, 'rw', ['news'], async () => {
        await news.news.filter(row => row.user?.id === id).delete();
        await news.news.filter(row => row.quote?.user?.id === id).modify(row => {
          row.quote = { id: row.quote.id, unavailable: true };
        });
      }),
    ]);
    if (failed || cleanups.some(result => result.status === 'rejected')) throw new Error('LOCAL_ACCOUNT_CLEANUP_PENDING');
    // Retain only the non-content tombstone to block obsolete asynchronous work.
    // It also makes a failed cleanup discoverable on the next app launch.
    appLocalStorage.setItem(localDeletionKey(id), 'complete');
  })().finally(() => { if (running.get(id) === operation) running.delete(id); });
  running.set(id, operation);
  return operation;
}

export async function retryPendingDeletionCleanup() {
  const ids = [];
  try {
    const prefix = storageName('account-deletion:local:');
    for (let index = 0; index < window.localStorage.length; index++) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(prefix) && window.localStorage.getItem(key) === 'pending') {
        const id = Number(key.slice(prefix.length));
        if (Number.isSafeInteger(id) && id > 0) ids.push(id);
      }
    }
  } catch { return; }
  await Promise.allSettled(ids.map(id => cleanupDeletedOwner(id)));
}
